/**
 * PHASE 9 — render-state for the Sun, Moon and planets.
 *
 * The dynamic solar-system bodies have no catalog coordinate: their J2000
 * position changes continuously. This module resolves each body once per
 * refresh interval (they move far slower than a frame) into a compact
 * render state consumed by the GPU body layer, the label layer, and
 * tap-to-identify — all in the SAME J2000 frame as the star catalog, so the
 * bodies land exactly where the renderer's star pipeline puts them.
 *
 * The position pipeline is:
 *
 *   topocentric of-date RA/Dec (astronomy-engine Equator)
 *     -> J2000 via the inverse precession rotation (ofDateToJ2000Rotation)
 *     -> renderer's standard J2000 xyz pipeline (skyFrame buildStarProjection)
 *
 * which is the same conversion SeeIt-mode guidance already uses
 * (bodyPositionJ2000 in catalog/dynamicObjects.ts).
 */

import { bodyEphemeris, PLANETS } from "../astro/ephemeris";
import { skyEngine } from "../astro/engine";
import type { ObserverLocation, ObserverTime, SolarSystemBody } from "../astro/types";
import { bodyFromId, bodyId, bodyPositionJ2000, DYNAMIC_BODIES } from "../catalog/dynamicObjects";
import { equatorialVector } from "./skyFrame";

/** How often body positions are recomputed (they drift < 1 arcmin/hour). */
export const BODY_REFRESH_MS = 2000;

export type BodyKind = "SUN" | "MOON" | "PLANET";

export interface BodyRenderState {
  /** Searchable id, e.g. "BODY:Jupiter". */
  id: string;
  /** Display name. */
  name: string;
  kind: BodyKind;
  body: SolarSystemBody;
  /** J2000 position (the renderer's native frame). */
  raHoursJ2000: number;
  decDegJ2000: number;
  magnitude: number;
  /** True angular diameter in arcseconds (Sun/Moon resolve; planets ~0). */
  angularDiameterArcseconds: number;
  /** Observer-relative horizontal position (diagnostics / HUD). */
  altitudeDeg: number;
  azimuthDeg: number;
}

export interface BodyAppearance {
  color: [number, number, number];
  /** Nominal sprite size in device pixels (SkyView-style marker). */
  sizePx: number;
}

/** Marker colours per body (warm white, pale grey, etc.). */
export const BODY_APPEARANCE: Readonly<Record<SolarSystemBody, BodyAppearance>> = {
  Sun: { color: [1.0, 0.92, 0.62], sizePx: 26 },
  Moon: { color: [0.92, 0.92, 0.86], sizePx: 22 },
  Mercury: { color: [0.76, 0.72, 0.66], sizePx: 10 },
  Venus: { color: [0.98, 0.92, 0.75], sizePx: 12 },
  Mars: { color: [0.94, 0.47, 0.33], sizePx: 12 },
  Jupiter: { color: [0.87, 0.74, 0.54], sizePx: 13 },
  Saturn: { color: [0.93, 0.86, 0.62], sizePx: 12 },
  Uranus: { color: [0.55, 0.85, 0.9], sizePx: 10 },
  Neptune: { color: [0.4, 0.53, 0.92], sizePx: 10 },
};

/**
 * Resolve every dynamic body for an observer/time. All 9 bodies (Sun, Moon,
 * 8 planets minus Pluto) are resolved in one pass so the GPU buffer, the
 * labels and picking all consume the identical positions.
 */
export function bodyRenderStates(observer: ObserverLocation, time: ObserverTime): BodyRenderState[] {
  return DYNAMIC_BODIES.map((body) => {
    const pos = bodyPositionJ2000(body, observer, time);
    const eph = bodyEphemeris(body, observer, time);
    const hor = skyEngine.bodyToHorizontal(body, observer, time, false);
    return {
      id: bodyId(body),
      name: body,
      kind: body === "Sun" ? "SUN" : body === "Moon" ? "MOON" : "PLANET",
      body,
      raHoursJ2000: pos.raHours,
      decDegJ2000: pos.decDeg,
      magnitude: eph.magnitude,
      angularDiameterArcseconds: eph.angularDiameterArcseconds,
      altitudeDeg: hor.altitudeDegrees,
      azimuthDeg: hor.azimuthDegrees,
    };
  });
}

/**
 * Interleaved GPU data [x, y, z, sizePx] for the body sprite layer, with
 * positions in the same J2000 equatorial xyz the star buffer uses. Sprite
 * sizes come from BODY_APPEARANCE (Sun/Moon keep their real angular size at
 * the current scale when it exceeds the marker minimum).
 */
export function bodySpriteData(states: readonly BodyRenderState[], canvasHeightPx: number, fovYDeg: number): Float32Array {
  const buf = new Float32Array(states.length * 4);
  const pxPerDeg = canvasHeightPx / Math.max(1, fovYDeg);
  for (let i = 0; i < states.length; i++) {
    const state = states[i]!;
    const v = equatorialVector(state.raHoursJ2000, state.decDegJ2000);
    const angularDeg = state.angularDiameterArcseconds / 3600;
    const angularPx = angularDeg * pxPerDeg;
    const appearance = BODY_APPEARANCE[state.body];
    const sizePx = Math.min(30, Math.max(appearance.sizePx, angularPx));
    buf[i * 4] = v[0];
    buf[i * 4 + 1] = v[1];
    buf[i * 4 + 2] = v[2];
    buf[i * 4 + 3] = sizePx;
  }
  return buf;
}

/** Packed RGB (one vec3 per body, same order as bodySpriteData). */
export function bodyColorData(states: readonly BodyRenderState[]): Float32Array {
  const buf = new Float32Array(states.length * 3);
  for (let i = 0; i < states.length; i++) {
    const c = BODY_APPEARANCE[states[i]!.body].color;
    buf[i * 3] = c[0];
    buf[i * 3 + 1] = c[1];
    buf[i * 3 + 2] = c[2];
  }
  return buf;
}

/**
 * Time-bucketed cache: positions are recomputed at most every
 * BODY_REFRESH_MS, or immediately when the observer location changes.
 */
export function createBodyStateCache() {
  let cached: BodyRenderState[] | null = null;
  let cachedAtMs = -Infinity;
  let cachedObserver: string | null = null;

  const observerKey = (observer: ObserverLocation): string =>
    `${observer.latitudeDegrees.toFixed(6)},${observer.longitudeDegrees.toFixed(6)},${observer.elevationMeters ?? 0}`;

  return {
    /** Resolve positions, reusing the last result within the refresh window. */
    get(observer: ObserverLocation, time: ObserverTime, nowMs = time.utcMilliseconds): BodyRenderState[] {
      const key = observerKey(observer);
      if (cached && key === cachedObserver && nowMs - cachedAtMs < BODY_REFRESH_MS) return cached;
      cached = bodyRenderStates(observer, time);
      cachedAtMs = nowMs;
      cachedObserver = key;
      return cached;
    },
    invalidate(): void {
      cached = null;
      cachedAtMs = -Infinity;
    },
  };
}

/** Resolve a body render state by its searchable id (picking / info card). */
export function bodyStateById(states: readonly BodyRenderState[], id: string): BodyRenderState | null {
  if (bodyFromId(id) === null) return null;
  for (const state of states) {
    if (state.id === id) return state;
  }
  return null;
}

export { DYNAMIC_BODIES, PLANETS };
